import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Leaf } from 'lucide-react';
import {
  CALCULATOR_DEFAULTS,
  buildCalculatorTransferPath,
  calculateWheelBlend,
  evaluatePrimarySpin,
  filterMathematicallyEligibleBalancers,
  filterPrimaryWheelEligible,
  formatPercentPoints,
  pickRandomStrain,
  planWheelSpin,
  surpriseBalancingStrain,
  type StrainSummary,
  type WheelAcceptedResult,
  type WheelBlendWeights,
  type WheelPrimaryEvaluationSuccess,
  type WheelStrainCandidate,
} from '@shared';
import { getProductRepository } from '../lib/productRepository';
import { useRevokeObjectUrls } from '../hooks/useRevokeObjectUrls';
import { revokeDisplayObjectUrls } from '../lib/productImages/objectUrls';
import { NumberStepper } from '../components/NumberStepper';
import { PrimaryButton, SecondaryButton } from '../components/ui';
import { StrainWheel } from '../components/StrainWheel';
import { resolveCardThumbnailUrl } from '../lib/cardThumbnail';

function toWheelCandidate(s: StrainSummary): WheelStrainCandidate {
  return {
    id: s.id,
    name: s.name,
    brand: s.brand,
    productName: s.productName,
    thcPercent: s.thcPercent,
    isArchived: s.isArchived,
    saveStatus: s.saveStatus,
    isBalancingStrain: s.isBalancingStrain,
  };
}

function displayName(s: WheelStrainCandidate): string {
  return s.name ?? s.productName ?? 'Untitled strain';
}

function summaryBlobUrls(rows: StrainSummary[]): Array<string | null> {
  return rows.flatMap((s) => [
    s.localPrimaryThumbnailUrl,
    s.firstLocalThumbnailUrl,
    s.primaryThumbnailUrl,
  ]);
}

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

function StrainThumb({
  strain,
}: {
  strain: StrainSummary | undefined;
}): JSX.Element {
  const [failed, setFailed] = useState<Set<string>>(() => new Set());
  const url = strain
    ? resolveCardThumbnailUrl(
        {
          spreadsheetUrl: strain.spreadsheetThumbnailUrl,
          localPrimaryUrl: strain.localPrimaryThumbnailUrl,
          firstLocalUrl: strain.firstLocalThumbnailUrl,
        },
        failed,
      )
    : null;
  if (!url) {
    return (
      <div className="wheel-thumb wheel-thumb--empty" aria-hidden>
        <Leaf size={28} strokeWidth={1.5} />
      </div>
    );
  }
  return (
    <img
      className="wheel-thumb"
      src={url}
      alt=""
      onError={() =>
        setFailed((prev) => {
          const next = new Set(prev);
          next.add(url);
          return next;
        })
      }
    />
  );
}

type ViewMode = 'targets' | 'result' | 'balancing' | 'blend_result';

/**
 * Spin the Wheel — dedicated tab: animated wheel, tradeoffs, balancing.
 */
export function SpinWheel(): JSX.Element {
  const navigate = useNavigate();
  const [strains, setStrains] = useState<StrainSummary[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [size, setSize] = useState(
    CALCULATOR_DEFAULTS.totalWeightGrams.toFixed(2),
  );
  const [target, setTarget] = useState(
    String(CALCULATOR_DEFAULTS.targetThcMg),
  );
  const precision = CALCULATOR_DEFAULTS.scalePrecisionGrams;

  const [view, setView] = useState<ViewMode>('targets');
  const [lastPrimaryId, setLastPrimaryId] = useState<string | null>(null);
  const [spinError, setSpinError] = useState<string | null>(null);
  const [evaluation, setEvaluation] =
    useState<WheelPrimaryEvaluationSuccess | null>(null);
  const [accepted, setAccepted] = useState<WheelAcceptedResult | null>(null);

  const [eligibleBalancers, setEligibleBalancers] = useState<
    WheelStrainCandidate[]
  >([]);
  const [selectedBalancer, setSelectedBalancer] =
    useState<WheelStrainCandidate | null>(null);
  const [blendWeights, setBlendWeights] = useState<WheelBlendWeights | null>(
    null,
  );
  const [blendError, setBlendError] = useState<string | null>(null);
  const [spinning, setSpinning] = useState(false);
  const [wheelRotation, setWheelRotation] = useState(0);
  const [spinDurationMs, setSpinDurationMs] = useState(0);
  const [highlightId, setHighlightId] = useState<string | null>(null);

  const pendingOutcomeRef = useRef<WheelPrimaryEvaluationSuccess | null>(null);
  const settleTimerRef = useRef<number | null>(null);
  const spinningRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    getProductRepository()
      .list({ archived: 'active', sort: 'name' })
      .then((rows) => {
        if (cancelled) {
          revokeDisplayObjectUrls(summaryBlobUrls(rows));
          return;
        }
        setStrains(rows);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setLoadError(
            err instanceof Error ? err.message : 'Could not load strains.',
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useRevokeObjectUrls(summaryBlobUrls(strains));

  useEffect(() => {
    return () => {
      if (settleTimerRef.current != null) {
        window.clearTimeout(settleTimerRef.current);
      }
    };
  }, []);

  const candidates = useMemo(
    () => strains.map(toWheelCandidate),
    [strains],
  );
  const primaryPool = useMemo(
    () => filterPrimaryWheelEligible(candidates),
    [candidates],
  );
  const strainById = useMemo(() => {
    const map = new Map<string, StrainSummary>();
    for (const s of strains) map.set(s.id, s);
    return map;
  }, [strains]);
  const sizeGrams = Number(size);
  const targetThcMg = Number(target);

  function clearSettleTimer(): void {
    if (settleTimerRef.current != null) {
      window.clearTimeout(settleTimerRef.current);
      settleTimerRef.current = null;
    }
  }

  function finishSpin(outcome: WheelPrimaryEvaluationSuccess): void {
    clearSettleTimer();
    spinningRef.current = false;
    pendingOutcomeRef.current = null;
    setLastPrimaryId(outcome.strain.id);
    setEvaluation(outcome);
    setHighlightId(outcome.strain.id);
    setView('result');
    setSpinning(false);
    setSpinDurationMs(0);
    if (outcome.evaluation.kind === 'exact_match') {
      setAccepted({
        kind: 'single',
        strainId: outcome.strain.id,
        sizeGrams: outcome.evaluation.totalWeightGrams,
        thcMg: outcome.evaluation.estimatedThcMg,
      });
    }
  }

  function settleSpin(): void {
    if (!spinningRef.current) return;
    const pending = pendingOutcomeRef.current;
    if (!pending) return;
    finishSpin(pending);
  }

  function runSpin(): void {
    if (spinningRef.current) return;

    setSpinError(null);
    setAccepted(null);
    setSelectedBalancer(null);
    setBlendWeights(null);
    setBlendError(null);
    setHighlightId(null);

    if (primaryPool.length === 0) {
      setSpinError(
        'No eligible strains are available. Active strains need a valid THC value greater than zero.',
      );
      setEvaluation(null);
      setView('targets');
      return;
    }

    const picked = pickRandomStrain(primaryPool, {
      excludeId: lastPrimaryId,
    });
    if (!picked) {
      setSpinError('Could not pick a strain.');
      return;
    }

    const selectedIndex = primaryPool.findIndex((s) => s.id === picked.id);
    if (selectedIndex < 0) {
      setSpinError('Could not map the selected strain to a wheel segment.');
      return;
    }

    const outcome = evaluatePrimarySpin(
      picked,
      sizeGrams,
      targetThcMg,
      precision,
    );
    if (!outcome.ok) {
      setSpinError(outcome.message);
      setEvaluation(null);
      return;
    }

    // Result is fixed before animation so timing cannot change the winner.
    const plan = planWheelSpin({
      selectedIndex,
      segmentCount: primaryPool.length,
      currentRotation: wheelRotation,
      preferReducedMotion: prefersReducedMotion(),
    });

    pendingOutcomeRef.current = outcome;
    spinningRef.current = true;
    setSpinning(true);
    setSpinDurationMs(plan.durationMs);
    setWheelRotation(plan.targetRotation);
    setView('targets');

    if (!plan.animate || plan.durationMs <= 0) {
      finishSpin(outcome);
      return;
    }

    clearSettleTimer();
    settleTimerRef.current = window.setTimeout(
      settleSpin,
      plan.durationMs + 120,
    );
  }

  function useKeepThc(): void {
    if (!evaluation || evaluation.evaluation.kind !== 'tradeoffs') return;
    const keep = evaluation.evaluation.keepThc;
    if (!keep.usable) return;
    setAccepted({
      kind: 'single',
      strainId: evaluation.strain.id,
      sizeGrams: keep.requiredSizeGrams,
      thcMg: keep.requestedThcMg,
    });
  }

  function useKeepSize(): void {
    if (!evaluation || evaluation.evaluation.kind !== 'tradeoffs') return;
    const keep = evaluation.evaluation.keepSize;
    setAccepted({
      kind: 'single',
      strainId: evaluation.strain.id,
      sizeGrams: keep.requestedSizeGrams,
      thcMg: keep.resultingThcMg,
    });
  }

  function openBalancing(): void {
    if (!evaluation) return;
    const eligible = filterMathematicallyEligibleBalancers(
      candidates,
      evaluation.strain,
      evaluation.targets,
    );
    setEligibleBalancers(eligible);
    setSelectedBalancer(null);
    setBlendWeights(null);
    setBlendError(null);
    setView('balancing');
  }

  function applyBalancer(balancer: WheelStrainCandidate): void {
    if (!evaluation) return;
    const calc = calculateWheelBlend(
      evaluation.strain,
      balancer,
      evaluation.targets,
    );
    if (!calc.ok) {
      setBlendError(calc.message);
      setBlendWeights(null);
      setSelectedBalancer(balancer);
      return;
    }
    setBlendError(null);
    setSelectedBalancer(balancer);
    setBlendWeights(calc.weights);
    setAccepted({
      kind: 'blend',
      primaryStrainId: evaluation.strain.id,
      balancingStrainId: balancer.id,
      primaryWeightGrams: calc.weights.primaryWeightGrams,
      balancingWeightGrams: calc.weights.balancingWeightGrams,
      totalWeightGrams: calc.weights.totalWeightGrams,
      thcMg: evaluation.targets.targetThcMg,
    });
    setView('blend_result');
  }

  function runSurprise(): void {
    const pick = surpriseBalancingStrain(eligibleBalancers, {
      excludeId: selectedBalancer?.id ?? null,
    });
    if (!pick) {
      setBlendError(
        'No eligible balancing strains can meet these targets with the selected strain.',
      );
      return;
    }
    applyBalancer(pick);
  }

  function useThisResult(): void {
    if (!accepted) return;
    navigate(buildCalculatorTransferPath(accepted));
  }

  const primarySummary = evaluation
    ? strainById.get(evaluation.strain.id)
    : undefined;

  return (
    <div className="stack calc-page wheel-page">
      <div>
        <h1 className="page-title">Spin the Wheel</h1>
        <p className="page-lead">
          Set your joint size and THC target, then spin for a random strain from
          your catalogue.
        </p>
      </div>

      {loadError && (
        <div className="notice" role="alert">
          {loadError}
        </div>
      )}

      <section className="card stack calc-step">
        <h2 className="calc-step__title">Targets</h2>
        <NumberStepper
          label="Joint size"
          value={size}
          onChange={setSize}
          step={precision}
          min={precision}
          suffix="g"
          decimals={2}
        />
        <NumberStepper
          label="Target THC"
          value={target}
          onChange={setTarget}
          step={1}
          min={1}
          suffix="mg"
          decimals={0}
        />
        <p className="hint muted">
          {primaryPool.length} eligible strain
          {primaryPool.length === 1 ? '' : 's'} in the wheel pool.
        </p>
      </section>

      <section className="card stack calc-step">
        <h2 className="calc-step__title">Strain wheel</h2>
        {primaryPool.length === 0 ? (
          <p className="notice" role="status">
            No eligible strains are available for the wheel. Active catalogue
            strains need a valid THC value greater than zero.
          </p>
        ) : (
          <StrainWheel
            strains={primaryPool}
            rotationDeg={wheelRotation}
            durationMs={spinDurationMs}
            spinning={spinning}
            selectedId={highlightId}
            onSpinSettled={settleSpin}
            onSpin={runSpin}
            spinDisabled={spinning || primaryPool.length === 0}
            spinLabel={
              spinning ? 'Spinning…' : evaluation ? 'Spin Again' : 'Spin'
            }
          />
        )}
        <PrimaryButton
          large
          block
          disabled={spinning || primaryPool.length === 0}
          onClick={runSpin}
        >
          {spinning ? 'Spinning…' : evaluation ? 'Spin Again' : 'Spin'}
        </PrimaryButton>
        {spinError && (
          <div className="notice" role="alert">
            {spinError}
          </div>
        )}
      </section>

      {evaluation && view !== 'targets' && (
        <section className="card stack calc-step" aria-live="polite">
          <h2 className="calc-step__title">Selected strain</h2>
          <div className="wheel-strain-row">
            <StrainThumb strain={primarySummary} />
            <div className="stack" style={{ gap: '0.25rem' }}>
              <strong>{displayName(evaluation.strain)}</strong>
              <span className="muted">
                {evaluation.strain.brand?.trim()
                  ? evaluation.strain.brand
                  : 'Brand not listed'}
              </span>
              <span>THC {formatPercentPoints(evaluation.thcPercent)}</span>
            </div>
          </div>
          <p className="hint">
            Targets: {evaluation.targets.targetSizeGrams.toFixed(2)} g ·{' '}
            {evaluation.targets.targetThcMg} mg THC
          </p>
        </section>
      )}

      {evaluation?.evaluation.kind === 'exact_match' && view === 'result' && (
        <section className="card stack calc-step">
          <h2 className="calc-step__title">Exact match</h2>
          <p>
            Required weight:{' '}
            <strong>
              {evaluation.evaluation.strainWeightGrams.toFixed(2)} g
            </strong>
          </p>
          <p>
            Total weight:{' '}
            <strong>
              {evaluation.evaluation.totalWeightGrams.toFixed(2)} g
            </strong>
          </p>
          <p>
            Estimated THC:{' '}
            <strong>
              {evaluation.evaluation.estimatedThcMg.toFixed(2)} mg
            </strong>
          </p>
          <p className="muted">
            Rounding difference:{' '}
            {evaluation.evaluation.roundingDifferenceMg >= 0 ? '+' : ''}
            {evaluation.evaluation.roundingDifferenceMg.toFixed(2)} mg
          </p>
          <div className="cluster" style={{ gap: '0.5rem' }}>
            <PrimaryButton onClick={useThisResult} disabled={spinning}>
              Use This Result
            </PrimaryButton>
            <SecondaryButton onClick={runSpin} disabled={spinning}>
              Spin Again
            </SecondaryButton>
          </div>
        </section>
      )}

      {evaluation?.evaluation.kind === 'tradeoffs' && view === 'result' && (
        <>
          <section className="card stack calc-step">
            <h2 className="calc-step__title">What this means</h2>
            <p>{evaluation.evaluation.explanation}</p>
          </section>

          <section className="card stack calc-step">
            <h2 className="calc-step__title">Keep the THC target</h2>
            {evaluation.evaluation.keepThc.usable ? (
              <>
                <p>
                  Keep {evaluation.evaluation.keepThc.requestedThcMg} mg ·
                  required size{' '}
                  <strong>
                    {evaluation.evaluation.keepThc.requiredSizeGrams.toFixed(2)}{' '}
                    g
                  </strong>
                </p>
                <p className="muted">
                  This is approximately{' '}
                  {Math.abs(
                    evaluation.evaluation.keepThc.sizeDifferenceGrams,
                  ).toFixed(2)}{' '}
                  g {evaluation.evaluation.keepThc.sizeRelation} than the
                  original size.
                </p>
                <SecondaryButton onClick={useKeepThc}>
                  Use This Size
                </SecondaryButton>
              </>
            ) : (
              <p className="notice" role="status">
                {evaluation.evaluation.keepThc.unusableReason ??
                  'This THC target is below the measurable scale step for this strain.'}
              </p>
            )}
          </section>

          <section className="card stack calc-step">
            <h2 className="calc-step__title">Keep the joint size</h2>
            <p>
              Keep{' '}
              {evaluation.evaluation.keepSize.requestedSizeGrams.toFixed(2)} g ·
              resulting THC{' '}
              <strong>
                {evaluation.evaluation.keepSize.resultingThcMg.toFixed(1)} mg
              </strong>
            </p>
            <p className="muted">
              This is approximately{' '}
              {Math.abs(
                evaluation.evaluation.keepSize.thcDifferenceMg,
              ).toFixed(1)}{' '}
              mg {evaluation.evaluation.keepSize.thcRelation} than the original
              target.
            </p>
            <SecondaryButton onClick={useKeepSize}>
              Use This THC Amount
            </SecondaryButton>
          </section>

          <section className="card stack calc-step">
            <h2 className="calc-step__title">Balancing strain</h2>
            <p className="hint">
              Keep this strain as the primary and add one labelled balancing
              strain to meet both original targets.
            </p>
            <SecondaryButton onClick={openBalancing}>
              Select Balancing Strain
            </SecondaryButton>
          </section>

          {accepted?.kind === 'single' && (
            <section className="card stack calc-step">
              <h2 className="calc-step__title">Accepted result</h2>
              <p>
                {accepted.sizeGrams.toFixed(2)} g · {accepted.thcMg.toFixed(1)}{' '}
                mg THC
              </p>
              <div className="cluster" style={{ gap: '0.5rem' }}>
                <PrimaryButton onClick={useThisResult}>
                  Use This Result
                </PrimaryButton>
                <SecondaryButton onClick={runSpin} disabled={spinning}>
                  Spin Again
                </SecondaryButton>
              </div>
            </section>
          )}
        </>
      )}

      {view === 'balancing' && evaluation && (
        <section className="card stack calc-step">
          <h2 className="calc-step__title">Choose a balancing strain</h2>
          <div className="cluster" style={{ gap: '0.5rem' }}>
            <SecondaryButton
              onClick={runSurprise}
              disabled={eligibleBalancers.length === 0}
            >
              Surprise Me
            </SecondaryButton>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => setView('result')}
            >
              Back to result
            </button>
          </div>
          {eligibleBalancers.length === 0 ? (
            <p className="notice">
              No eligible balancing strain can satisfy the current size and THC
              targets with this primary strain. Balancing strains must be
              labelled in the spreadsheet and able to form a valid blend.
            </p>
          ) : (
            <ul className="stack wheel-balancer-list">
              {eligibleBalancers.map((b) => {
                const summary = strainById.get(b.id);
                return (
                  <li key={b.id}>
                    <button
                      type="button"
                      className="wheel-balancer-option"
                      onClick={() => applyBalancer(b)}
                    >
                      <StrainThumb strain={summary} />
                      <span className="stack" style={{ gap: '0.15rem' }}>
                        <strong>{displayName(b)}</strong>
                        <span className="muted">
                          {b.brand?.trim() ? b.brand : 'Brand not listed'}
                        </span>
                        <span>
                          THC{' '}
                          {formatPercentPoints(
                            typeof b.thcPercent === 'number' ? b.thcPercent : 0,
                          )}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          {blendError && (
            <div className="notice" role="alert">
              {blendError}
            </div>
          )}
        </section>
      )}

      {view === 'blend_result' &&
        evaluation &&
        selectedBalancer &&
        blendWeights && (
          <section className="card stack calc-step">
            <h2 className="calc-step__title">Blend result</h2>
            <p>
              Primary ({displayName(evaluation.strain)}):{' '}
              <strong>{blendWeights.primaryWeightGrams.toFixed(2)} g</strong> (
              {(blendWeights.primaryProportion * 100).toFixed(0)}%)
            </p>
            <p>
              Balancing ({displayName(selectedBalancer)}):{' '}
              <strong>
                {blendWeights.balancingWeightGrams.toFixed(2)} g
              </strong>{' '}
              ({(blendWeights.balancingProportion * 100).toFixed(0)}%)
            </p>
            <p>
              Total:{' '}
              <strong>{blendWeights.totalWeightGrams.toFixed(2)} g</strong>
            </p>
            <p>
              Estimated THC:{' '}
              <strong>{blendWeights.estimatedThcMg.toFixed(2)} mg</strong>
            </p>
            <p className="muted">
              Rounding difference:{' '}
              {blendWeights.roundingDifferenceMg >= 0 ? '+' : ''}
              {blendWeights.roundingDifferenceMg.toFixed(2)} mg
            </p>
            <div
              className="cluster"
              style={{ gap: '0.5rem', flexWrap: 'wrap' }}
            >
              <PrimaryButton onClick={useThisResult}>
                Use This Result
              </PrimaryButton>
              <SecondaryButton onClick={() => setView('balancing')}>
                Choose another balancer
              </SecondaryButton>
              <SecondaryButton onClick={runSurprise}>
                Surprise Me again
              </SecondaryButton>
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setView('result')}
              >
                Back to primary result
              </button>
              <button
                type="button"
                className="btn btn-ghost"
                onClick={runSpin}
                disabled={spinning}
              >
                Spin again
              </button>
            </div>
          </section>
        )}

      <p className="muted">
        <Link to="/calculator">Open Calculator</Link>
      </p>
    </div>
  );
}
